import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStaffAssignedStreams, assignStaffToStream } from "../../services/stream.service";
import { getMyInvitations } from "../../services/user.service";

import { getAllStaffs, getUsersList, createStaff, deleteStaff, searchTeamStaffs } from "../../../advertiser/services/user.service";
import moment from "moment";
import { Placeholder } from "rsuite";
import Swal from "sweetalert2";
import Card from "../../components/Card";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import { SOCKET_EVENTS, ROLE } from "../../../../utils/Constants";
import { getUser } from "../../../../utils/commonfunction";

const TABS = [
    { key: "live", label: "Live" },
    { key: "incoming", label: "Upcoming" },
    { key: "past", label: "Past" },
];

const PER_PAGE = 12;

export default function AssignedStreams() {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("live");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ live: [], incoming: [], past: [] });
    const [totalCounts, setTotalCounts] = useState({ live: 0, incoming: 0, past: 0 });

    // Memoize currentUser to prevent infinite loops (getUser returns a new object each time)
    const currentUser = useMemo(() => {
        let user = getUser(ROLE.ADVERTISER);
        if (!user || !user.token) user = getUser(ROLE.USER);
        return user;
    }, []);
    console.log(currentUser, "currentUser");

    const isDirectAdvertiser = useMemo(() => {
        return !!(currentUser?.is_kyc_verified || currentUser?.isAdvertiser || currentUser?.role === ROLE.ADVERTISER);
    }, [currentUser]);

    const initialSellerId = location.state?.seller_id || (isDirectAdvertiser ? currentUser?._id : null);
    const initialSellerName = location.state?.seller_name || (isDirectAdvertiser ? (currentUser?.store_name || currentUser?.name || "My Store") : null);

    const [sellerId, setSellerId] = useState(initialSellerId);
    const [sellerName, setSellerName] = useState(initialSellerName);
    const [activePermissions, setActivePermissions] = useState([]);
    const [myAcceptedInvites, setMyAcceptedInvites] = useState([]);
    const [isResolvingContext, setIsResolvingContext] = useState(!initialSellerId);

    // Strict Ownership Check to prevent permission leakage across stores
    const isOwner = useMemo(() => {
        return isDirectAdvertiser && String(currentUser?._id) === String(sellerId);
    }, [isDirectAdvertiser, currentUser, sellerId]);
    console.log(isOwner, "isOwner");

    const hasStreamAllocation = useMemo(() => {
        return isOwner || activePermissions.includes("stream_allocation");
    }, [isOwner, activePermissions]);

    const [allStaff, setAllStaff] = useState([]);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedStream, setSelectedStream] = useState(null);
    const [staffSearchQuery, setStaffSearchQuery] = useState("");
    const [tempSelectedStaffIds, setTempSelectedStaffIds] = useState([]);
    const [originalStaffIds, setOriginalStaffIds] = useState([]);
    const [globalSearchResults, setGlobalSearchResults] = useState([]);
    const [isSearchingGlobal, setIsSearchingGlobal] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [invitationEmail, setInvitationEmail] = useState("");
    const [selectedPermissions, setSelectedPermissions] = useState(["accept_product", "edit_product", "delete_product", "reply_on_comments"]);

    const fetchAssignedStreams = useCallback(async (tab = activeTab, pg = page) => {
        if (!sellerId) return;
        try {
            setLoading(true);
            const resp = await getStaffAssignedStreams({
                seller_id: sellerId,
                _id: sellerId,
                tab,
                page: pg,
                per_page: PER_PAGE,
            });

            if (resp.success && resp.data) {
                const { live = [], incoming = [], past = [], totalCounts: tc = {} } = resp.data;
                setData({ live, incoming, past });
                setTotalCounts({
                    live: tc.live || 0,
                    incoming: tc.incoming || 0,
                    past: tc.past || 0,
                });
            } else {
                Swal.fire({ icon: "error", text: resp.message || "Failed to fetch streams" });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", text: "Something went wrong fetching assigned streams." });
        } finally {
            setLoading(false);
        }
    }, [sellerId, activeTab, page]);

    // Resolve seller context and permissions asynchronously
    useEffect(() => {
        let isMounted = true;

        const resolveContext = async () => {
            try {
                const resp = await getMyInvitations();
                if (!isMounted) return;

                if (resp && resp.success && Array.isArray(resp.data)) {
                    const accepted = resp.data.filter(
                        (inv) => inv.status === "Accepted" || (!inv.status && inv.seller_id)
                    );
                    setMyAcceptedInvites(accepted);

                    // If explicit seller_id was passed via navigation state
                    if (location.state?.seller_id) {
                        const targetId = location.state.seller_id;
                        const matchedInvite = accepted.find(
                            (inv) => String(inv.seller_id?._id || inv.seller_id || inv._id) === String(targetId)
                        );
                        const targetSeller = matchedInvite?.seller_id;
                        setSellerId(targetId);
                        setSellerName(
                            location.state.seller_name || targetSeller?.store_name || targetSeller?.name || "The Store"
                        );
                        if (location.state.permissions) {
                            const raw = location.state.permissions;
                            const parsed = typeof raw === "string" ? (raw.startsWith("[") ? JSON.parse(raw) : [raw]) : raw;
                            setActivePermissions(parsed || []);
                        } else if (matchedInvite?.permissions) {
                            const raw = matchedInvite.permissions;
                            const parsed = typeof raw === "string" ? (raw.startsWith("[") ? JSON.parse(raw) : [raw]) : raw;
                            setActivePermissions(parsed || []);
                        }
                        setIsResolvingContext(false);
                        return;
                    }

                    if (accepted.length > 0) {
                        // Prioritize external staff invitation if available (e.g. from adv99), else first
                        const externalInvite = accepted.find(
                            (inv) => String(inv.seller_id?._id || inv.seller_id || inv._id) !== String(currentUser?._id)
                        );
                        const chosenInvite = externalInvite || accepted[0];
                        const targetSeller = chosenInvite.seller_id;
                        const sId = targetSeller?._id || chosenInvite.seller_id || chosenInvite._id;
                        const sName = targetSeller?.store_name || targetSeller?.name || chosenInvite.store_name || "The Store";

                        setSellerId(sId);
                        setSellerName(sName);

                        const raw = chosenInvite.permissions;
                        const parsed = typeof raw === "string" ? (raw.startsWith("[") ? JSON.parse(raw) : [raw]) : raw;
                        setActivePermissions(parsed || []);
                        setIsResolvingContext(false);
                        return;
                    }

                    const pending = resp.data.filter((inv) => inv.status === "Pending");
                    if (pending.length > 0) {
                        Swal.fire({
                            icon: "info",
                            title: "Pending Invitations",
                            text: "You have pending staff invitations. Please accept an invitation first to view assigned streams.",
                            confirmButtonColor: "#FF63B5",
                        }).then(() => {
                            navigate("/user/staff-invitations");
                        });
                        return;
                    }
                }

                // If user is a direct advertiser with own store and no staff invites
                if (isDirectAdvertiser) {
                    setSellerId(currentUser?._id);
                    setSellerName(currentUser?.store_name || currentUser?.name || "My Store");
                    setActivePermissions(currentUser?.permissions || []);
                    setIsResolvingContext(false);
                    return;
                }

                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: "No advertiser context found. Please navigate from your staff invitations.",
                    confirmButtonColor: "#FF63B5",
                }).then(() => {
                    navigate(-1);
                });
            } catch (err) {
                console.error("Error resolving staff/seller context:", err);
                if (isMounted) {
                    if (isDirectAdvertiser) {
                        setSellerId(currentUser?._id);
                        setSellerName(currentUser?.store_name || currentUser?.name || "My Store");
                        setActivePermissions(currentUser?.permissions || []);
                        setIsResolvingContext(false);
                        return;
                    }
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text: "Failed to load seller context. Please try again.",
                        confirmButtonColor: "#FF63B5",
                    }).then(() => {
                        navigate(-1);
                    });
                }
            } finally {
                if (isMounted) setIsResolvingContext(false);
            }
        };

        resolveContext();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!isResolvingContext && sellerId) {
            fetchAssignedStreams(activeTab, page);
        }
    }, [sellerId, isResolvingContext, activeTab, page, fetchAssignedStreams]);

    const getStaffs = useCallback(async () => {
        try {
            const resp = await getAllStaffs();
            if (resp.success) {
                const list = resp.data?.list || (Array.isArray(resp.data) ? resp.data : []);
                setAllStaff(list);
            }
        } catch (err) {
            console.error("Error fetching staff:", err);
        }
    }, [getAllStaffs]);

    useEffect(() => {
        if (hasStreamAllocation) {
            getStaffs();
        }
    }, [hasStreamAllocation, getStaffs]);

    useEffect(() => {
        const handleBroadcast = (broadcast) => {
            if (broadcast?.event === SOCKET_EVENTS.STREAM_STARTED || broadcast?.event === SOCKET_EVENTS.STREAM_ENDED) {
                fetchAssignedStreams(activeTab, page);
            }
        };
        SOCKET_CLIENT.on(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);
        return () => {
            SOCKET_CLIENT.off(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);
        };
    }, [sellerId, activeTab, page, fetchAssignedStreams]);

    useEffect(() => {
        // If not focused and no query, don't search to avoid background requests
        if (!isSearchFocused && !staffSearchQuery) {
            setGlobalSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setIsSearchingGlobal(true);
                const resp = await searchTeamStaffs({ search: staffSearchQuery });
                if (resp.success) {
                    setGlobalSearchResults(resp.data || []);
                }
            } catch (err) {
                console.error("Global search error:", err);
            } finally {
                setIsSearchingGlobal(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [staffSearchQuery, isSearchFocused]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
    };

    const handleGoLive = (stream) => {
        const isPast = activeTab === "past" || stream.autoStatus === "past";
        const isUpcoming = activeTab === "incoming" || stream.autoStatus === "upcoming";
        navigate(`/user/staff-stream/${stream._id}`, {
            state: {
                isPast,
                isUpcoming,
                permissions: activePermissions // Pass store-specific permissions forward
            }
        });
    };

    const handleAssignStaff = (stream) => {
        setSelectedStream(stream);

        // Filter: ONLY Keep IDs that are actually in our team (allStaff) AND are NOT the stream's own ID
        const validStaffIdsInTeam = (allStaff || []).map(s => s._id.toString());
        const cleanedIds = (stream.staff_ids || [])
            .filter(id => id.toString() !== stream._id.toString())
            .filter(id => validStaffIdsInTeam.includes(id.toString()));

        setTempSelectedStaffIds(cleanedIds);
        setOriginalStaffIds(cleanedIds);
        setStaffSearchQuery("");
        setGlobalSearchResults([]);
        setInvitationEmail("");
        // setShowInviteForm(false);
        setIsAssignModalOpen(true);
    };

    const handleInviteGlobalUser = async (user) => {
        try {
            setLoading(true);
            const resp = await createStaff({
                email: user.email,
                name: user.name || user.email.split("@")[0],
                permissions: selectedPermissions
            });

            if (resp.success) {
                // Refresh the entire staff list to show the newly added member
                await getStaffs();
                setGlobalSearchResults(prev => prev.filter(u => u.email !== user.email));
                setStaffSearchQuery(""); // Clear input and close popup on selection

                Swal.fire({
                    icon: "success",
                    text: "Staff invited to team.",
                    toast: true,
                    position: "top-end",
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({ icon: "error", text: resp.message || "Failed to invite user." });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", text: "Something went wrong inviting user." });
        } finally {
            setLoading(false);
        }
    };

    const handleInviteByEmail = async () => {
        if (!invitationEmail || !invitationEmail.includes("@")) {
            Swal.fire({ icon: "warning", text: "Please enter a valid email address." });
            return;
        }
        await handleInviteGlobalUser({ email: invitationEmail });
        setInvitationEmail("");
        // setShowInviteForm(false);
    };

    const handleDeleteStaff = async (staffId) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This will remove this staff member from your team entirely.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#FF63B5",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete"
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const resp = await deleteStaff({ _id: staffId });
                if (resp.success) {
                    setAllStaff(prev => prev.filter(s => s._id !== staffId));
                    const newStaffIds = tempSelectedStaffIds.filter(id => id !== staffId);
                    setTempSelectedStaffIds(newStaffIds);
                    // Update streams list too
                    setData(prevData => {
                        const updatedStreams = prevData[activeTab].map(s =>
                            s._id === selectedStream._id ? { ...s, staff_ids: newStaffIds } : s
                        );
                        return { ...prevData, [activeTab]: updatedStreams };
                    });
                    Swal.fire({ icon: "success", text: "Staff removed from team.", toast: true, position: "top-end", timer: 2000, showConfirmButton: false });
                } else {
                    Swal.fire({ icon: "error", text: resp.message || "Failed to delete staff." });
                }
            } catch (err) {
                console.error(err);
                Swal.fire({ icon: "error", text: "Something went wrong." });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleToggleStaffAssignment = (staffId) => {
        const sId = staffId.toString();
        setTempSelectedStaffIds(prev =>
            prev.includes(sId)
                ? prev.filter(id => id !== sId)
                : [...prev, sId]
        );
    };

    const handleSaveAssignment = async () => {
        try {
            setLoading(true);

            // Double check validation before sending
            const validStaffIdsInTeam = (allStaff || []).map(s => s._id.toString());
            const finalStaffIds = tempSelectedStaffIds
                .filter(id => id.toString() !== selectedStream._id.toString())
                .filter(id => validStaffIdsInTeam.includes(id.toString()));

            const resp = await assignStaffToStream(selectedStream._id, finalStaffIds);

            if (resp.success) {
                // Update local data state across tabs
                setData(prevData => {
                    const newData = { ...prevData };
                    Object.keys(newData).forEach(tab => {
                        if (Array.isArray(newData[tab])) {
                            newData[tab] = newData[tab].map(s =>
                                s._id === selectedStream._id ? { ...s, staff_ids: finalStaffIds } : s
                            );
                        }
                    });
                    return newData;
                });

                setOriginalStaffIds(finalStaffIds);
                setTempSelectedStaffIds(finalStaffIds);

                Swal.fire({
                    icon: "success",
                    text: "Staff assignments saved successfully.",
                    toast: true,
                    position: "top-end",
                    timer: 2000,
                    showConfirmButton: false
                });
                setIsAssignModalOpen(false);
            } else {
                Swal.fire({ icon: "error", text: resp.message || "Failed to save assignments." });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", text: "Something went wrong saving assignments." });
        } finally {
            setLoading(false);
        }
    };



    const togglePermission = (perm) => {

        setSelectedPermissions(prev =>
            prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
        );
    };

    const PERMISSION_OPTIONS = [
        { label: "Accept Product", value: "accept_product" },
        { label: "Edit Product", value: "edit_product" },
        { label: "Delete Product", value: "delete_product" },
        { label: "Reply Comments", value: "reply_on_comments" }
    ];


    const streams = data[activeTab === "incoming" ? "incoming" : activeTab] || [];
    const totalCount = totalCounts[activeTab === "incoming" ? "incoming" : activeTab] || 0;
    const totalPages = Math.ceil(totalCount / PER_PAGE);

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex items-center justify-center gap-2 mt-8">
                <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                >
                    ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((item, idx) =>
                        item === "..." ? (
                            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                        ) : (
                            <button
                                key={item}
                                onClick={() => setPage(item)}
                                className={`px-3 py-1.5 rounded-md border text-sm transition ${item === page
                                    ? "border-[var(--secondary-500)] bg-[var(--secondary-500)] text-white"
                                    : "border-gray-300 hover:bg-gray-100"
                                    }`}
                            >
                                {item}
                            </button>
                        )
                    )}
                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                >
                    Next →
                </button>
            </div>
        );
    };

    if (isResolvingContext) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto min-h-screen">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-gray-200 pb-[15px]">
                <h1 className="headings-h1 text-zinc-950">
                    Assigned Streams for {sellerName}
                </h1>

                {myAcceptedInvites.length > 1 && (
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Store:</label>
                        <select
                            value={sellerId || ""}
                            onChange={(e) => {
                                const newId = e.target.value;
                                const inv = myAcceptedInvites.find(
                                    (i) => String(i.seller_id?._id || i.seller_id || i._id) === String(newId)
                                );
                                if (inv) {
                                    const targetSeller = inv.seller_id;
                                    const sId = targetSeller?._id || inv.seller_id || inv._id;
                                    const sName = targetSeller?.store_name || targetSeller?.name || inv.store_name || "The Store";
                                    setSellerId(sId);
                                    setSellerName(sName);
                                    const raw = inv.permissions;
                                    const parsed = typeof raw === "string" ? (raw.startsWith("[") ? JSON.parse(raw) : [raw]) : raw;
                                    setActivePermissions(parsed || []);
                                    setPage(1);
                                }
                            }}
                            className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FF63B5]"
                        >
                            {myAcceptedInvites.map((inv) => {
                                const targetSeller = inv.seller_id;
                                const id = targetSeller?._id || inv.seller_id || inv._id;
                                const name = targetSeller?.store_name || targetSeller?.name || inv.store_name || "The Store";
                                return (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
            </div>

            {/* Tabs */}
            {/* bg-[var(--secondary-500)] text-white border-[var(--secondary-500)] */}
            <div className="flex gap-2 mb-8 border-b border-gray-200 justify-center flex items-center overflow-x-auto no-scrollbar">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={`px-6 py-3 text-base font-semibold whitespace-nowrap border-b-2 transition-all duration-300 ${activeTab === tab.key
                            ? "border-[var(--secondary-500)] text-[var(--secondary-500)]"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        {tab.label}
                        {totalCounts[tab.key] > 0 && (
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${activeTab === tab.key ? "bg-[var(--secondary-500)] text-white" : "bg-gray-200 text-gray-600"
                                }`}>
                                {totalCounts[tab.key]}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {loading && !isAssignModalOpen ? (
                <div className="flex items-center justify-center py-24">
                    <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : streams.length === 0 ? (
                <div className="text-center py-20 bg-zinc-50 rounded-xl border border-gray-100">
                    <p className="text-gray-500 text-lg">
                        No {activeTab === "incoming" ? "upcoming" : activeTab} streams found.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[20px] lg:gap-[25px]">
                        {streams.map((stream) => (
                            <div key={stream._id} className="flex flex-col gap-2">
                                <div>
                                    <Card
                                        images={stream.cover_image || stream.thumbnail || "/images/cameraimg.svg"}
                                        title={stream.title}
                                        subtitle={stream.seller?.store_name || sellerName}
                                        live={stream.autoStatus === "live"}
                                        date={moment(stream.planned_date || stream.date).format("MMMM DD, YYYY [at] hh:mm A")}
                                        onClick={() => handleGoLive(stream)}
                                        onButtonClick={() => handleGoLive(stream)}
                                    />
                                </div>
                                {hasStreamAllocation && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleAssignStaff(stream); }}
                                        className="mt-1 w-full flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium text-zinc-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-zinc-950 transition-all shadow-sm"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" x2="19" y1="8" y2="14" /><line x1="22" x2="16" y1="11" y2="11" /></svg>
                                        Assign Staff
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {renderPagination()}
                </>
            )}

            {/* Assign Staff Modal */}
            {isAssignModalOpen && (
                <dialog open className="modal p-[15px]" aria-modal>
                    <div className="modal-box max-w-[500px] !w-full">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="headings-h2 text-zinc-800">Assign Staff</h2>
                            <button onClick={() => setIsAssignModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <img src="/images/close.svg" alt="close" className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="mb-6 relative">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search staff to add..."
                                    className="form-control-custom !pr-[40px]"
                                    value={staffSearchQuery}
                                    onChange={(e) => setStaffSearchQuery(e.target.value)}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                                />
                                <img src="/images/search1.svg" alt="search" className="absolute right-3 top-2.5 w-5 h-5 opacity-40" />
                            </div>

                            {/* Unified Search Results Dropdown */}
                            {(globalSearchResults.length > 0 || isSearchingGlobal) && isSearchFocused && (
                                <div className="absolute z-20 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-2xl overflow-hidden max-h-[350px] overflow-y-auto custom-scrollbar anim-fade-in-up">
                                    <div className="p-2 flex flex-col gap-1">
                                        {/* Local Team Matches */}

                                        {/* Global Results */}
                                        {globalSearchResults.length > 0 && (
                                            <>
                                                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 py-1.5 flex items-center gap-2">
                                                    <div className="w-1 h-1 rounded-full bg-[#FF63B5]"></div> Team Search
                                                </h3>
                                                {globalSearchResults.filter(u => !(tempSelectedStaffIds || []).some(id => id.toString() === u._id.toString())).map(u => (
                                                    <div
                                                        key={u._id}
                                                        className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-zinc-50 cursor-pointer transition-colors border border-transparent hover:border-gray-100 group"
                                                        onClick={() => {
                                                            handleToggleStaffAssignment(u._id);
                                                            setStaffSearchQuery("");
                                                            setGlobalSearchResults([]);
                                                        }}
                                                    >
                                                        <div className="h-9 w-9 rounded-full overflow-hidden bg-zinc-100 flex-shrink-0 border border-black/5">
                                                            <img 
                                                                src={u.profile_image ? (u.profile_image.startsWith('http') ? u.profile_image : `/uploads/users/${u.profile_image}`) : "/images/User.png"} 
                                                                onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                                                                alt={u.name} 
                                                                className="h-full w-full object-cover" 
                                                            />
                                                        </div>
                                                        <div className="flex flex-col flex-1 min-w-0">
                                                            <span className="text-[13px] font-bold text-gray-900 leading-tight truncate">{u.name || "User"}</span>
                                                            <span className="text-[11px] text-gray-500 truncate">{u.email}</span>
                                                        </div>
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <span className="text-[10px] font-extrabold text-[#FF63B5] uppercase px-2 py-1 bg-[#FF63B5]/10 rounded-md">Add & Assign</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        {isSearchingGlobal && (
                                            <div className="p-3 text-center">
                                                <span className="text-xs text-gray-400 animate-pulse font-medium">Searching team members...</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="max-h-[460px] overflow-y-auto mb-2 pr-2 custom-scrollbar flex flex-col gap-6">

                            {/* <div className="bg-gray-50/80 p-4 rounded-xl border border-gray-100 shadow-inner">
                                <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3 px-1 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                    Permissions for New Invites
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    {PERMISSION_OPTIONS.map(opt => (
                                        <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                                            <div className="relative flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPermissions.includes(opt.value)}
                                                    onChange={() => togglePermission(opt.value)}
                                                    className="w-4 h-4 rounded border-gray-300 text-[#FF63B5] focus:ring-[#FF63B5] cursor-pointer"
                                                />
                                            </div>
                                            <span className="text-[12px] text-gray-600 group-hover:text-gray-900 transition-colors uppercase font-bold tracking-tight">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div> */}

                            {/* Assigned Staff Section */}
                            <div>
                                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">Assigned Staff Team</h3>
                                {(!allStaff || allStaff.length === 0 || !(allStaff.some(s => (tempSelectedStaffIds || []).some(id => id.toString() === s._id.toString())))) ? (
                                    <div className="py-12 text-center border-2 border-dashed border-gray-100 rounded-3xl bg-gray-50/30">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                                        </div>
                                        <p className="text-sm text-gray-400 font-medium">No staff assigned yet.</p>
                                        <p className="text-[11px] text-gray-300 mt-1">Search team members above to assign them.</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {allStaff
                                            .filter(s => (tempSelectedStaffIds || []).some(id => id.toString() === s._id.toString()))
                                            .map(s => {
                                                const isAssigned = (tempSelectedStaffIds || []).some(id => id.toString() === s._id.toString());
                                                return (
                                                    <div
                                                        key={s._id}
                                                        className={`flex items-center gap-4 p-4 rounded-2xl bg-white border transition-all group ${isAssigned ? "border-[#FF63B5] bg-[#FF63B5]/5 shadow-sm" : "border-gray-100 shadow-sm hover:shadow-md"}`}
                                                    >
                                                        <div className="h-11 w-11 rounded-full overflow-hidden bg-zinc-100 flex-shrink-0 border border-black/5 shadow-inner">
                                                            <img 
                                                                src={s.profile_image || "/images/User.png"} 
                                                                onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                                                                alt={s.name} 
                                                                className="h-full w-full object-cover" 
                                                            />
                                                        </div>
                                                        <div className="flex flex-col flex-1 min-w-0">
                                                            <span className="text-[14px] font-extrabold text-gray-900 leading-tight truncate">{s.name}</span>
                                                            <span className="text-[12px] text-gray-500 truncate">{s.email}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleToggleStaffAssignment(s._id)}
                                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                                                title="Deselect staff"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>

                            {/* Invite by Email Section */}
                            {/* <div className="pt-2 border-t border-gray-100">
                                {!showInviteForm ? (
                                    <button
                                        onClick={() => setShowInviteForm(true)}
                                        className="w-full py-3.5 px-4 text-sm font-bold text-gray-500 bg-gray-50 rounded-2xl border border-dashed border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all flex items-center justify-center gap-2 shadow-sm"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6" /><path d="M22 11h-6" /></svg>
                                        Invite New Staff Member
                                    </button>
                                ) : (
                                    <div className="bg-gray-50 p-2 rounded-2xl border border-gray-200 shadow-inner">
                                        <label className="text-[11px] font-bold text-gray-400 uppercase mb-3 block px-1 tracking-widest">Send Invitation Email</label>
                                        <div className="flex flex-col gap-3">
                                            <div className="flex gap-2">
                                                <input
                                                    type="email"
                                                    placeholder="Enter email address..."
                                                    className="form-control-custom !h-12 !text-sm flex-1 !bg-white !shadow-none !border-gray-200 focus:!border-[#FF63B5] transition-all"
                                                    value={invitationEmail}
                                                    onChange={(e) => setInvitationEmail(e.target.value)}
                                                />
                                                <button
                                                    onClick={handleInviteByEmail}
                                                    className="px-8 py-2.5 bg-[#FF63B5] text-white text-sm font-semibold rounded-lg hover:bg-[#e0559f] transition-all shadow-md active:scale-95"
                                                >
                                                    Invite
                                                </button>
                                                <button
                                                    onClick={() => setShowInviteForm(false)}
                                                    className="px-2 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-gray-400 italic px-1">Invitation will be sent with the core permissions selected above.</p>
                                        </div>
                                    </div>
                                )}
                            </div> */}
                        </div>

                        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-50">
                            {JSON.stringify([...tempSelectedStaffIds].sort()) !== JSON.stringify([...originalStaffIds].sort()) ? (
                                <>
                                    <button
                                        onClick={() => {
                                            setTempSelectedStaffIds(originalStaffIds);
                                            setIsAssignModalOpen(false);
                                        }}
                                        className="px-6 py-2.5 text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveAssignment}
                                        disabled={loading}
                                        className="px-8 py-2.5 bg-[#FF63B5] text-white text-sm font-semibold rounded-lg hover:bg-[#e0559f] transition-all shadow-md active:scale-95 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Saving...
                                            </>
                                        ) : "Save Changes"}
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setIsAssignModalOpen(false)}
                                    className="px-10 py-2.5 bg-[#FF63B5] text-white text-sm font-semibold rounded-lg hover:bg-[#e0559f] transition-all shadow-md active:scale-95"
                                >
                                    Close
                                </button>
                            )}
                        </div>

                    </div>
                </dialog>
            )}
        </div>
    );
}